import React from 'react';
import { NavLink } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import type { useProductFilters } from '../../hooks/useProductFilters';
import { categories } from '../../data/categories';
import { capacityBuckets, priceBuckets } from '../../data/filters';
import { products } from '../../data/products';
type FilterApi = ReturnType<typeof useProductFilters>;
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <fieldset className="border-b border-line py-5 first:pt-0 last:border-0" data-id="element-531" data-mp="1qbgxh3ukqq9j">
      <legend className="mb-3 text-sm font-semibold text-ink" data-id="element-532" data-mp="1qdjl5h8987b3">{title}</legend>
      {children}
    </fieldset>;
}
function Check({
  checked,
  onChange,
  label,
  count,
  type = 'checkbox',
  name
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  count?: number;
  type?: 'checkbox' | 'radio';
  name?: string;
}) {
  return <label className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm text-ink" data-id="element-533" data-mp="292hu3l8829px">
      <input type={type} name={name} checked={checked} onChange={onChange} className="peer sr-only" data-id="element-534" data-mp="2trqqgg8asxkb" />
      <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center border transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-ink/30 ${type === 'radio' ? 'rounded-full' : 'rounded'} ${checked ? 'border-ink bg-ink' : 'border-ink/25 bg-white'}`} aria-hidden="true" data-id="element-535" data-mp="2otp7rwesb09d">
        {checked && <CheckIcon className="h-3 w-3 text-volt" strokeWidth={3} data-id="element-536" data-mp="3ckkvoig4dbbc" />}
      </span>
      <span className="flex-1" data-id="element-537" data-mp="37jf2kjijrm1a">{label}</span>
      {count !== undefined && <span className="text-xs text-muted" data-id="element-538" data-mp="3liepnmqevsvc">{count}</span>}
    </label>;
}
export function FilterPanel({
  api
}: {
  api: FilterApi;
}) {
  const {
    filters,
    setFilters,
    toggleBrand,
    brandOptions,
    showCapacity
  } = api;
  return <div data-id="element-539" data-mp="1r5oxb9foyrq2">
      <Section title="Category" data-id="element-540" data-mp="1r7hu7x0i5xkv">
        <ul className="space-y-0.5" data-id="element-541" data-mp="1n43el7gh7j7y">
          <li data-id="element-542" data-mp="1n54x1wztn7zp">
            <NavLink to="/shop" end className={({
            isActive
          }) => `flex justify-between rounded-md px-2 py-1.5 text-sm ${isActive ? 'bg-surface font-semibold text-ink' : 'text-ink/80 hover:text-ink'}`} data-id="element-543" data-mp="1n5mqymz0ha4g">
              All products <span className="text-xs text-muted" data-id="element-544" data-mp="bv2g1udjgj">{products.length}</span>
            </NavLink>
          </li>
          {categories.map(c => <li key={c.id} data-id="element-545" data-mp="4y3pphtvfjof">
              <NavLink to={`/shop/${c.id}`} className={({
            isActive
          }) => `flex justify-between rounded-md px-2 py-1.5 text-sm ${isActive ? 'bg-surface font-semibold text-ink' : 'text-ink/80 hover:text-ink'}`} data-id="element-546" data-mp="92l8pesepsy5">
                {c.name}
                <span className="text-xs text-muted" data-id="element-547" data-mp="npwipc8xx4w5">{products.filter(p => p.category === c.id).length}</span>
              </NavLink>
            </li>)}
        </ul>
      </Section>

      <Section title="Brand" data-id="element-548" data-mp="wqupu3eodsiy">
        {brandOptions.map(([brand, count]) => <Check key={brand} label={brand} count={count} checked={filters.brands.includes(brand)} onChange={() => toggleBrand(brand)} data-id="element-549" data-mp="2h9eub6a9t1k0" />)}
      </Section>

      <Section title="Price" data-id="element-550" data-mp="2da64udm9jpu9">
        {priceBuckets.map(b => <Check key={b.id} type="radio" name="price" label={b.label} checked={filters.price === b.id} onChange={() => setFilters(f => ({
        ...f,
        price: f.price === b.id ? null : b.id
      }))} data-id="element-551" data-mp="289rugox6w3t2" />)}
      </Section>

      {showCapacity && <Section title="Storage capacity" data-id="element-552" data-mp="2vzcoc2uvslh4">
          {capacityBuckets.map(b => <Check key={b.id} type="radio" name="capacity" label={b.label} checked={filters.capacity === b.id} onChange={() => setFilters(f => ({
        ...f,
        capacity: f.capacity === b.id ? null : b.id
      }))} data-id="element-553" data-mp="2r69wbz5r5k31" />)}
        </Section>}

      <Section title="Availability" data-id="element-554" data-mp="2zffb65z97077">
        <Check label="In stock (ships tomorrow)" checked={filters.inStock} onChange={() => setFilters(f => ({
        ...f,
        inStock: !f.inStock
      }))} data-id="element-555" data-mp="2zecl209r91sc" />
        <Check label="On sale" checked={filters.onSale} onChange={() => setFilters(f => ({
        ...f,
        onSale: !f.onSale
      }))} data-id="element-556" data-mp="33ewlbcpmklip" />
      </Section>
    </div>;
}